import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntil } from 'rxjs';
import { ToastService } from 'src/app/shared-new/services/toast.service';
import { UserModel } from '../../models/vet-users/user.model';
import { CommonService } from '../../services/common.service';
import { HttpErrorHandlerService } from '../../services/http-error-handler.service';
import { BaseUnsubscribeComponent } from '../../shared-new/components/base-unsubscribe/base-unsubscribe.component';
import { UserFacade } from '../../shared-new/store/user-management/user.facade';
import { CountryISO, PhoneNumberFormat, SearchCountryField, NgxIntlTelInputModule } from 'ngx-intl-tel-input';
import { LookupService } from '../../services/lookup.service';
import { UserService } from '../../services/user.service';
import { NotificationTypeDto, UserNotificationType } from '../../shared-new/enums/notification-type.enum';

@Component({
  selector: 'app-user-detail',
  templateUrl: './user-detail.component.html',
  styleUrls: ['./user-detail.component.scss'],
})
export class UserDetailComponent
  extends BaseUnsubscribeComponent
  implements OnInit
{
  public isLoader: boolean = false;
  public submitted = false;
  public SearchCountryField = SearchCountryField;
  public CountryISO = CountryISO;
  public PhoneNumberFormat = PhoneNumberFormat;
  public selectedCountryISO: string;
  
  public preferredCountries: CountryISO[] = [CountryISO.UnitedStates, CountryISO.Australia, CountryISO.NewZealand, CountryISO.SouthAfrica, CountryISO.France];
  usersError$ = this.userFacade.usersError$;
  usersLoading$ = this.userFacade.usersLoading$;
  success$ = this.userFacade.success$;
  user$ = this.userFacade.user$;
  user: UserModel;
  public userProfileForm: FormGroup;
  public countries: any[];
  public notificationTypes: UserNotificationType[];
  constructor(
    private userFacade: UserFacade,
    private httpErrorHandlerService: HttpErrorHandlerService,
    public readonly router: Router,
    private activatedRoute: ActivatedRoute,
    private toastService: ToastService,
    private commonService: CommonService,
    private lookupService: LookupService,
    private userService: UserService,
    private fb: FormBuilder
  ) {
    super();
  }
  private loadCountries() {
    this.commonService.getCountries().subscribe((response) => {
      if (response.body) {
        const responseData = JSON.parse(JSON.stringify(response.body));
        if (responseData) {
          this.countries = responseData;
          this.attachEvents();
        }
      }
    });
  }
  private loadNotificationTypes(userId: number) {
    this.lookupService.getUserNotificationTypes(userId).subscribe((response) => {
      this.notificationTypes = response;

      const notifArray = this.fb.array(
        this.notificationTypes.map(nt =>
          this.fb.group({
            notificationTypeId: [nt.notificationTypeId],
            isEnable: [nt.isEnable],
            name: [nt.name]
          })
        )
      );

      this.userProfileForm.setControl('notifications', notifArray);
    });
  }

  public get notificationsFormArray(): FormArray {
    return this.userProfileForm.get('notifications') as FormArray;
  }
  private setDefaultCountryFromDialCode(code: string): string {
    var countryCode = '';
    if (code) {
      const dialCode = code.replace('+', '');
      const matched = (window as any).intlTelInputGlobals.getCountryData().find((c: any) => c.dialCode === dialCode);

      if (matched) {
        countryCode = matched.iso2.toUpperCase() as CountryISO;
      } 
    }
    return countryCode;
  }
// Simple country code detection - expand as needed
  private getCountryCodeFromPhone(phoneNumber: string): string {
    const matchedCountry = typeof phoneNumber === 'undefined' || phoneNumber === '' ? '' : this.countries.find(country =>
      phoneNumber.startsWith(country.code));

  return matchedCountry?.code ?? ''; // Default
}
  private attachEvents() {
    this.user$.pipe(takeUntil(this.unsubscribe$)).subscribe((user) => {
      if (user) {
        
        this.user = user;
        this.userProfileForm = UserModel.asFormGroup(user, this.notificationTypes);        
      const phoneValue = this.userProfileForm.get('phoneNumber')?.value;
      const countryCodeValue = this.userProfileForm.get('countryCode')?.value;
      this.selectedCountryISO=  phoneValue != null ? this.setDefaultCountryFromDialCode(countryCodeValue).trim(): null;
      this.userProfileForm.get('phoneNumberControl')?.setValue( phoneValue != null ?phoneValue.replace(countryCodeValue, '').trim(): null);
      this.userProfileForm.get('phoneNumber')?.setValue(
        countryCodeValue != null && phoneValue != null 
          ? countryCodeValue + ' ' + phoneValue 
          : null
      );
      
      }
    });
    this.usersLoading$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((loading) => {
        this.isLoader = loading;
      });
    this.usersError$.pipe(takeUntil(this.unsubscribe$)).subscribe((err) => {
      if (err) {
        this.httpErrorHandlerService.ShowErrorMessage(err);
      }
    });
    this.success$.pipe(takeUntil(this.unsubscribe$)).subscribe((response) => {
      if (response?.saveUser) {
        this.toastService.success('User has been saved successfully');

        this.router.navigate(['/users']);
      }
    });
  }
  ngOnInit(): void {
    this.userProfileForm = UserModel.asFormGroup(null,[]);
    const userId = Number(this.activatedRoute.snapshot.paramMap.get('id'));
    if (userId > 0) {
      this.userFacade.getUser(userId);
    } else {
      this.userFacade.getUser(0);
    }
    this.loadCountries();
    this.loadNotificationTypes(userId);
    
  }
  submitForm(form: any): void {
    this.submitted = true;
    
    if (this.userProfileForm.invalid) {
      return;
    }
    
    const userId = Number(this.activatedRoute.snapshot.paramMap.get('id'));
    form.userId = userId;
    form.phoneNumber = form.phoneNumberControl?.internationalNumber || form.phoneNumber;
    form.countryCode= this.getCountryCodeFromPhone(form.phoneNumberControl?.internationalNumber);
    form.phoneNumber = form.phoneNumber.replace(form.countryCode, '').trim();
    const notificationSettings: { [key: number]: boolean } =
      form.notifications.reduce((acc: any, nt: any) => {
        acc[nt.notificationTypeId] = nt.isEnable;
        return acc;
      }, {});
    const model = form as UserModel;
    model.notificationSettings = notificationSettings;
    this.userFacade.saveUser(model);
  }
}
